<script>
import {computed, defineComponent, ref} from "vue";
import { useApiCall } from "@/composables/useApiCall";
import { infoPagesApiCall, deletePagesItemApiCall } from "@/services/settings";
import Skeleton from "primevue/skeleton";
import Button from "primevue/button";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import {useStore} from "vuex";

export default defineComponent({
  name: "SettingsMain",
  components: {ConfirmationModal, Button, Skeleton},
  setup() {
    const store = useStore();
    const lang = computed(() => store.getters.getLang);
    const titleField = computed(() => `title_${lang.value}`);
    const deleteId = ref(0)
    const {
      isLoading: isInfoPagesLoading,
      data: infoPagesData,
      error: infoPagesError,
      executeApiCall: getInfoPagesAction,
    } = useApiCall(infoPagesApiCall, false)

    const {
      isLoading: isDeleteInfoPagesLoading,
      data: deleteInfoPagesData,
      error: deleteInfoPagesError,
      executeApiCall: deleteInfoPagesAction,
    } = useApiCall(deletePagesItemApiCall, true)

    const deleteItem = (id) => {
      deleteId.value = id
    }

    const changeVisibleModal = () => {
      deleteId.value = 0
    }

    const deleteCategory = async () => {
      try {
        await deleteInfoPagesAction({id: deleteId.value})
        changeVisibleModal()
        await getInfoPagesAction()
      } catch {
        console.error(deleteInfoPagesError.value)
      }
    }


    return {
      isInfoPagesLoading,
      infoPagesData,
      deleteItem,
      changeVisibleModal,
      deleteCategory,
      deleteId,
      isDeleteInfoPagesLoading,
      titleField,
    };
  }
})
</script>

<template>
  <ConfirmationModal :visible="!!deleteId">
    <template #header>
      Удалить пункт меню
    </template>
    <template #default>
      <span>Вы уверены, что хотите удалить пункт меню? Это действие будет невозможно отменить</span>
    </template>
    <template #footer>
      <div class="flex justify-content-between">
        <Button label="Отменить" @click="changeVisibleModal" class="btn-primary-outlined font-light w-12"/>
        <Button label="Удалить" :loading="isDeleteInfoPagesLoading" @click="deleteCategory" class="btn-primary font-light ml-3 w-12"/>
      </div>
    </template>
  </ConfirmationModal>
  <section class="settings-main">
    <div v-if="infoPagesData" class="settings-main__list">
      <router-link :to="{name: 'info-page-add'}" class="settings-main__add">
        <svg class="btn-primary__svg" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 4V20M20 12H3.99998" stroke="#C3987A" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <span>Добавить пункт меню</span>
      </router-link>
      <div v-for="item in infoPagesData.data" :key="item.id" class="settings-main__item">
        <span class="settings-main__item-title">{{item.title_ru}}</span>
        <span class="settings-main__item-type">Тип контента: {{item.type === 'link' ? 'ссылка': 'текст'}}</span>
        <div class="settings-main__item-actions">
          <router-link :to="{name: 'info-page-item', params: {id: item.id}}" class="settings-main__item-link">
            Редактировать
          </router-link>
          <button @click="deleteItem(item.id)" class="settings-main__item-delete">
            <svg width="25" height="25" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.2679 5.36049H14.7322C14.7322 4.76849 14.497 4.20074 14.0784 3.78213C13.6598 3.36352 13.092 3.12835 12.5 3.12835C11.908 3.12835 11.3403 3.36352 10.9217 3.78213C10.503 4.20074 10.2679 4.76849 10.2679 5.36049ZM8.92859 5.36049C8.92859 4.41329 9.30486 3.50488 9.97464 2.83511C10.6444 2.16534 11.5528 1.78906 12.5 1.78906C13.4472 1.78906 14.3556 2.16534 15.0254 2.83511C15.6952 3.50488 16.0714 4.41329 16.0714 5.36049H21.6518C21.8294 5.36049 21.9997 5.43104 22.1253 5.55662C22.2509 5.68221 22.3214 5.85253 22.3214 6.03013C22.3214 6.20773 22.2509 6.37806 22.1253 6.50364C21.9997 6.62923 21.8294 6.69978 21.6518 6.69978H20.4822L19.3956 19.7382C19.3165 20.6867 18.8839 21.5708 18.1835 22.2153C17.4831 22.8598 16.5661 23.2176 15.6143 23.2176H9.38573C8.43394 23.2176 7.51693 22.8598 6.81654 22.2153C6.11615 21.5708 5.68354 20.6867 5.60448 19.7382L4.51787 6.69978H3.34823C3.17063 6.69978 3.00031 6.62923 2.87472 6.50364C2.74914 6.37806 2.67859 6.20773 2.67859 6.03013C2.67859 5.85253 2.74914 5.68221 2.87472 5.55662C3.00031 5.43104 3.17063 5.36049 3.34823 5.36049H8.92859ZM6.9393 19.6266C6.99037 20.2403 7.27022 20.8125 7.72336 21.2296C8.17651 21.6467 8.76985 21.8782 9.38573 21.8783H15.6143C16.2302 21.8782 16.8235 21.6467 17.2767 21.2296C17.7298 20.8125 18.0097 20.2403 18.0607 19.6266L19.1393 6.69978H5.86162L6.9393 19.6266ZM10.4911 9.82478C10.6687 9.82478 10.839 9.89533 10.9646 10.0209C11.0902 10.1465 11.1607 10.3168 11.1607 10.4944V18.0837C11.1607 18.2613 11.0902 18.4316 10.9646 18.5572C10.839 18.6828 10.6687 18.7533 10.4911 18.7533C10.3135 18.7533 10.1432 18.6828 10.0176 18.5572C9.892 18.4316 9.82145 18.2613 9.82145 18.0837V10.4944C9.82145 10.3168 9.892 10.1465 10.0176 10.0209C10.1432 9.89533 10.3135 9.82478 10.4911 9.82478ZM15.1786 10.4944C15.1786 10.3168 15.108 10.1465 14.9825 10.0209C14.8569 9.89533 14.6865 9.82478 14.5089 9.82478C14.3313 9.82478 14.161 9.89533 14.0354 10.0209C13.9099 10.1465 13.8393 10.3168 13.8393 10.4944V18.0837C13.8393 18.2613 13.9099 18.4316 14.0354 18.5572C14.161 18.6828 14.3313 18.7533 14.5089 18.7533C14.6865 18.7533 14.8569 18.6828 14.9825 18.5572C15.108 18.4316 15.1786 18.2613 15.1786 18.0837V10.4944Z" fill="#E26B6B"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
    <Skeleton v-else height="187px"></Skeleton>
  </section>
</template>
<style lang="scss" scoped>
.settings-main {
  &__list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 30px;
    @media screen and (min-width: 800px) {
      grid-template-columns: repeat(2, 1fr);
    }
    @media screen and (min-width: 1024px) {
      grid-template-columns: repeat(3, 1fr);
    }
    @media screen and (min-width: 1600px) {
      grid-template-columns: repeat(4, 1fr);
    }
  }
  &__item {
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 10px;
    padding: 16px;
    &-delete {
      background: white;
      border: none;
      padding: 0;
      cursor: pointer;
    }
    &-title {
      font-weight: 600;
      font-size: 18px;
      line-height: 27px;
      color: #333333;
    }
    &-type {
      font-weight: 400;
      font-size: 14px;
      line-height: 21px;
      color: #999999;
    }
    &-link {
      color: var(--color-primary);
      font-weight: 600;
      font-size: 13px;
      line-height: 20px;
      margin-top: 5px;
    }
    &-actions {
      display: flex;
      justify-content: space-between;
      margin-top: 11px;
    }
  }
  &__add {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    height: 116px;
    border-radius: 10px;
    border: 1px dashed var(--color-primary);
    color: var(--color-primary);
    text-decoration: none;
  }
}
</style>